import { DEFAULT_FARM_MANAGER_SETTINGS } from "../../database/farmManagerSettings";
import {
        summarizeConversation,
        formatSummaryForPrompt,
        shouldSummarize,
        getRecentMessagesToKeep,
} from "./conversationSummarizer";

/**
 * Enhanced history configuration
 */
const HISTORY_CONFIG = {
        // Keep last N messages in full detail when summarizing
        RECENT_MESSAGES_FULL: 6,
        // Default max messages if not specified in settings
        DEFAULT_MAX_MESSAGES: 12,
};

function normalizeMessage(message) {
        if (!message) {
                return null;
        }

        const role = message.sender === "user" ? "user" : "model";
        const content = typeof message.content === "string" ? message.content.trim() : "";

        if (!content) {
                return null;
        }

        return {
                role,
                parts: [{ text: content }],
        };
}

function squashConsecutiveRoles(messages) {
        return messages.reduce((acc, current) => {
                if (!current) {
                        return acc;
                }

                const last = acc[acc.length - 1];
                if (last && last.role === current.role) {
                        acc[acc.length - 1] = current;
                        return acc;
                }

                acc.push(current);
                return acc;
        }, []);
}

/**
 * Trims conversation to ensure proper user-model alternation
 * @param {Array} messages - Normalized messages
 * @returns {Array} Trimmed messages
 */
function trimConversationEnds(messages) {
        const trimmed = [...messages];

        // Ensure conversation starts with user message
        while (trimmed[0]?.role === "model") {
                trimmed.shift();
        }

        // Ensure conversation ends with model message (for history)
        while (trimmed[trimmed.length - 1]?.role === "user") {
                trimmed.pop();
        }

        return trimmed;
}

/**
 * Builds conversation history for Gemini, with basic windowing
 * @param {Array} conversationHistory - Raw conversation history
 * @param {Object} settings - Farm manager settings
 * @returns {Array} Formatted history for Gemini
 */
export function buildConversationHistory(conversationHistory = [], settings = null) {
        if (!Array.isArray(conversationHistory) || conversationHistory.length === 0) {
                return [];
        }

        const conversationSettings = settings?.conversation || DEFAULT_FARM_MANAGER_SETTINGS.conversation;
        const maxHistoryMessages = conversationSettings.maxHistoryMessages || HISTORY_CONFIG.DEFAULT_MAX_MESSAGES;

        const normalized = conversationHistory
                .slice(-maxHistoryMessages)
                .map(normalizeMessage)
                .filter(Boolean);

        const squashed = squashConsecutiveRoles(normalized);
        return trimConversationEnds(squashed);
}

/**
 * Enhanced history builder with conversation summarization support
 *
 * When conversation exceeds threshold, older messages are summarized
 * and only recent messages are kept in full detail.
 *
 * @param {Array} conversationHistory - Raw conversation history
 * @param {Object} settings - Farm manager settings
 * @param {string} language - Language code for summary formatting
 * @returns {Object} { geminiHistory: Array, contextSummary: string|null }
 */
export function buildHistoryWithContext(conversationHistory = [], settings = null, language = "en") {
        if (!Array.isArray(conversationHistory) || conversationHistory.length === 0) {
                return {
                        geminiHistory: [],
                        contextSummary: null,
                };
        }

        const conversationSettings = settings?.conversation || DEFAULT_FARM_MANAGER_SETTINGS.conversation;
        const maxHistoryMessages = conversationSettings.maxHistoryMessages || HISTORY_CONFIG.DEFAULT_MAX_MESSAGES;
        const recentToKeep = getRecentMessagesToKeep();

        // If conversation is short enough, return all messages without summarization
        if (conversationHistory.length <= recentToKeep) {
                const normalized = conversationHistory
                        .map(normalizeMessage)
                        .filter(Boolean);

                return {
                        geminiHistory: trimConversationEnds(squashConsecutiveRoles(normalized)),
                        contextSummary: null,
                };
        }

        // Split into older messages (to summarize) and recent messages (to keep in full)
        const olderMessages = conversationHistory.slice(0, -recentToKeep);
        const recentMessages = conversationHistory.slice(-recentToKeep);

        // Generate summary of older messages
        let contextSummary = null;
        if (shouldSummarize(olderMessages.length)) {
                const summary = summarizeConversation(olderMessages);
                if (summary) {
                        contextSummary = formatSummaryForPrompt(summary, language);
                }
        }

        // Process recent messages normally
        const normalized = recentMessages
                .slice(-maxHistoryMessages)
                .map(normalizeMessage)
                .filter(Boolean);

        return {
                geminiHistory: trimConversationEnds(squashConsecutiveRoles(normalized)),
                contextSummary,
        };
}

/**
 * Extracts context from conversation for enhanced prompting
 * @param {Array} conversationHistory - Raw conversation history
 * @returns {Object} Extracted context including topics and entities
 */
export function extractConversationContext(conversationHistory = []) {
        if (!Array.isArray(conversationHistory) || conversationHistory.length === 0) {
                return {
                        topics: [],
                        animals: [],
                        groups: [],
                };
        }

        // Import dynamically to avoid circular dependencies
        const { extractConversationTopics } = require("./conversationSummarizer");
        return extractConversationTopics(conversationHistory);
}
